'use client';

import { useEffect, useState, useCallback } from 'react';

const TIME_UPDATE_INTERVAL = 1000;

const formatTimeUnit = (value: number): string => {
  return Math.max(0, value).toString().padStart(2, '0');
};

export type CountdownTime = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
};

export const useCountdown = (targetTime: Date): CountdownTime => {
  const [timeLeft, setTimeLeft] = useState<CountdownTime>({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
  });
  const targetTimeMs = targetTime.getTime();

  const calculateTimeLeft = useCallback((targetMs: number): CountdownTime => {
    const difference = targetMs - Date.now();

    if (difference <= 0) {
      return {
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00',
      };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / 1000 / 60) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    return {
      days: formatTimeUnit(days),
      hours: formatTimeUnit(hours),
      minutes: formatTimeUnit(minutes),
      seconds: formatTimeUnit(seconds),
    };
  }, []);

  useEffect(() => {
    setTimeLeft(calculateTimeLeft(targetTimeMs));
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetTimeMs));
    }, TIME_UPDATE_INTERVAL);

    return () => clearInterval(timer);
  }, [targetTimeMs, calculateTimeLeft]);

  return timeLeft;
};
